import { useCallback, useEffect, useState } from "react";
import { Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Skeleton } from "./ui/skeleton";

const MEMBER_ROLES = ["TEAM_MEMBER", "PM", "LEAD", "ADMIN"];
const EMPTY = { user_id: "", project_role: "", allocation_percent: 100, reporting_required: true };
const slug = (email) => (email || "").split("@")[0];

function AddMemberForm({ projectId, existingIds, onAdded }) {
  const { t } = useLang();
  const [options, setOptions] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/users/options").then((r) => setOptions(r.data)).catch(() => setOptions([]));
  }, []);
  const candidates = options.filter((u) => MEMBER_ROLES.includes(u.role) && !existingIds.includes(u.id));

  const add = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/members`, {
        ...form, project_role: form.project_role || "Team member",
        allocation_percent: Number(form.allocation_percent),
      });
      toast.success(t("memberAdded"));
      setForm(EMPTY);
      onAdded();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="border rounded-md p-3 space-y-3 bg-secondary/30" data-testid="add-member-form">
      <div className="text-xs font-semibold tracking-wide text-muted-foreground uppercase flex items-center gap-1.5">
        <UserPlus className="h-3.5 w-3.5" />{t("addMember")}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1 sm:col-span-2"><Label>{t("selectUser")}</Label>
          <Select value={form.user_id || undefined} onValueChange={(v) => setForm({ ...form, user_id: v })}>
            <SelectTrigger data-testid="member-user-select"><SelectValue placeholder={t("selectUser")} /></SelectTrigger>
            <SelectContent>
              {candidates.map((u) => (
                <SelectItem key={u.id} value={u.id} data-testid={`member-user-option-${u.id}`}>{u.name} · {u.role}</SelectItem>
              ))}
            </SelectContent>
          </Select></div>
        <div className="space-y-1"><Label>{t("projectRole")}</Label>
          <Input data-testid="member-role-input" placeholder="Backend Developer" value={form.project_role}
            onChange={(e) => setForm({ ...form, project_role: e.target.value })} /></div>
        <div className="space-y-1"><Label>{t("allocation")}</Label>
          <Input data-testid="member-allocation-input" type="number" min={0} max={100} value={form.allocation_percent}
            onChange={(e) => setForm({ ...form, allocation_percent: e.target.value })} /></div>
      </div>
      <div className="flex items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm">
          <Switch data-testid="member-reporting-switch" checked={form.reporting_required}
            onCheckedChange={(v) => setForm({ ...form, reporting_required: v })} />
          {t("reportingRequired")}
        </label>
        <Button size="sm" data-testid="member-add-btn" className="bg-[#1E3A8A] hover:bg-[#1c3479]"
          disabled={!form.user_id || busy} onClick={add}>{t("addMember")}</Button>
      </div>
    </div>
  );
}

function MemberRow({ m, projectId, canManage, onChanged }) {
  const { t } = useLang();
  const remove = async () => {
    try {
      await api.delete(`/projects/${projectId}/members/${m.id}`);
      toast.success(t("memberRemoved"));
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="flex items-center gap-3 py-2" data-testid={`member-row-${slug(m.user_email)}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{m.user_name} <span className="text-xs text-muted-foreground">· {m.user_role}</span></div>
        <div className="text-xs text-muted-foreground truncate">{m.project_role} · {m.allocation_percent}%{m.reporting_required ? "" : ` · ${t("reportingRequired")}: ${t("helpNo")}`}</div>
      </div>
      {canManage && (
        <Button size="sm" variant="ghost" className="h-7 text-xs text-[#991B1B]" data-testid={`member-remove-${slug(m.user_email)}`} onClick={remove}>
          <Trash2 className="h-3.5 w-3.5 mr-1" />{t("remove")}
        </Button>
      )}
    </div>
  );
}

export default function ProjectMembersDialog({ project, open, onOpenChange, onChanged }) {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    if (!project?.id) return;
    api.get(`/projects/${project.id}/members`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [project?.id]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const changed = () => { load(); onChanged?.(); };
  if (!project) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" aria-describedby={undefined} data-testid="members-dialog">
        <DialogHeader><DialogTitle className="font-heading">{t("members")} — {project.key}</DialogTitle></DialogHeader>
        {!data ? <Skeleton className="h-32 w-full" /> : (
          <div className="space-y-4">
            <div className="divide-y" data-testid="members-list">
              {data.members.map((m) => (
                <MemberRow key={m.id} m={m} projectId={project.id} canManage={data.can_manage} onChanged={changed} />
              ))}
              {data.members.length === 0 && (
                <div className="py-4 text-sm text-muted-foreground" data-testid="members-empty">{t("noMembers")}</div>
              )}
            </div>
            {data.can_manage && (
              <AddMemberForm projectId={project.id} existingIds={data.members.map((m) => m.user_id)} onAdded={changed} />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
